(ns app.domain.form.model
  (:require #?(:clj  [core.feature-flag.core :as feature-flag]
               :cljs [core.front.feature-flags :as ff])
            #?(:clj [app.domain.form.schema-filter :as form-schema-filter])
            #?(:clj [core.db.entity-export :as entity-export])
            #?(:clj [core.db.trail :as trail])
            #?(:clj [core.util.edn :as edn])
            #?(:clj [datomic.api :as d])
            #?(:cljs [app.front.util :as futil])
            [app.domain.form.schema :as form-schema]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [app.domain.party.model :as party-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.i18n.model :as i18n-model]
            [app.rich-text :as rich-text]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]
            [core.permission.resolver :as permission-resolver]
            [core.util.string :as str-util]
            [potpuri.core :as p]))

(defn- get-field-set [fields current]
  (let [member? (comp (partial = (:db/id current))
                      :entity/parent
                      val)]
    (->> fields
         (filter member?)
         (map #(->> % key (get fields))))))

(defn- get-field-seq [fields current]
  (->> (get-field-set fields current)
       (sort-by :form.field/index)))

(defn- get-field-map [fields current]
  (->> (get-field-set fields current)
       (reduce (fn [acc member]
                 (assoc acc (:form.field/name member) member))
               {})))

(defn get-field
  "Accepts map of fields keyed by `:db/id` and a current field. Based on field type, returns
  the current field, a sequence of fields, a sorted sequence of fields, or a map of fields keyed
  by member field name."
  [fields current-field]
  (case (-> current-field :form.field/kind)
    :form.field.kind/val current-field
    :form.field.kind/set (get-field-set fields current-field)
    :form.field.kind/seq (get-field-seq fields current-field)
    :form.field.kind/map (get-field-map fields current-field)))

(defn get-form-data-schema [form]
  ;;TODO ALWAYS USE THIS FN WHEN READING DATA FROM FORMS?
  #?(:clj  (form-schema-filter/get-valid-form-data-schema form)
     :cljs (-> form :form/schema :form.schema/data-schema)))

(defn form->field-config-map
  "Build a form's schema field-config map: field-id -> render-shape config
  (see `app.domain.form.schema/get-fields-from-schema`, which also documents its shape and the
  caveats for duplicated ids). Keyed by bare field id, so the duplicate-id ambiguity applies:
  a table column id that also exists elsewhere in the schema resolves to a last-wins blend."
  [form]
  (->> form
       get-form-data-schema
       form-schema/get-fields-from-schema))

(defn form->field-ids-from-schema [form]
  (->> (form->field-config-map form)
       keys
       set))

(defn field-attrs-from-schema [field]
  (some-> (:form/_form-fields field)
          first
          get-form-data-schema
          (form-schema/find-field (:form.field/id field))))

(defn field-config-by-field-key [form field-key]
  (when field-key
    (some-> form
            get-form-data-schema
            (form-schema/find-field field-key))))

(defn is-empty? [value]
  (str/blank? (str value)))

(defn field-comparison-key [field]
  (select-keys field [:form.field/id :form.field/index :form.field/name]))

(defn- collection-member-key [field]
  (assoc (field-comparison-key field) :form.field/kind (:form.field/kind field)))

;; Comparisons accept both raw field values and collection wrappers. Use namespaced
;; keys and sentinels to distinguish internal comparison data from ordinary field values.
(defn collection-value
  ([fields value-fn] (collection-value fields value-fn #{}))
  ([fields value-fn empty-keys]
   {::children (merge (zipmap empty-keys (repeat nil))
                      (->> fields
                           (group-by collection-member-key)
                           (p/map-vals (fn [matches]
                                         (if (= 1 (count matches))
                                           (value-fn (first matches))
                                           ::not-comparable)))))}))

(defn- collection-value? [value]
  (and (map? value) (contains? value ::children)))

(defn field-values-differ?
  "Compare matched schema fields, but retain indexed row and internal named-member changes.
   An unsupported or ambiguous matched value makes the comparison unavailable."
  [baseline current]
  (cond
    (or (= ::not-comparable baseline) (= ::not-comparable current)) nil
    (and (collection-value? baseline) (collection-value? current))
    (let [before      (::children baseline)
          after       (::children current)
          keys-a      (set (keys before))
          keys-b      (set (keys after))
          shared      (set/intersection keys-a keys-b)
          changes     (map #(field-values-differ? (get before %) (get after %)) shared)
          structural? (some #(or (contains? % :form.field/index) (contains? % :form.field/name))
                            (set/union (set/difference keys-a keys-b) (set/difference keys-b keys-a)))]
      (when-not (some nil? changes)
        (boolean (or structural? (some true? changes)))))
    :else (not= baseline current)))

(defn decode-value [field]
  (let [personal-id? (-> field :form.field/kind (= :form.field.kind/encrypted-personal-id))
        value        (if personal-id?
                       (:form.field/personal-id field)
                       (:form.field/value field))]
    #?(:clj  (if personal-id?
               (entity-export/decrypt-and-mask-personal-id value)
               (edn/decode value))
       :cljs value)))

(declare condition-true?)

(defn single-condition-true? [field-config form-fields generation condition]
  (every? (fn [[field-id required-value]]
            (let [field (p/find-first form-fields
                                      #(-> % :form.field/id (= field-id)))]
              (when (->> (get field-config field-id)
                         :condition
                         (condition-true? field-config form-fields generation))
                (if (= required-value :ANY-VALUE)
                  field
                  (or (= (decode-value field)
                         required-value)
                      (= (str (decode-value field))
                         (str required-value)))))))
          (:field condition)))

(defn condition-true?
  "Check if `condition` is fulfilled (or `nil`) within the `form-fields` provided, taking their visibility into
  account recursively based on `field-config`. E.g.:
  condition is {:field-b \"Yes\"}, i.e. requires that field B = Yes, and:
     {:form.field/id    :field-b
      :form.field/value \"Yes\"}
     => true

     {:form.field/id    :field-b
      :form.field/value \"No\"}
    => false

  but if :field-b itself depends on :field-c value \"Yes\", and
     {:form.field/id    :field-c
      :form.field/value \"No\"}
    => false

  i.e. :field-b is hidden and thus the original `condition` is not fulfilled either, regardless of :field-b value"
  ([field-config form-fields condition]
   (condition-true? field-config form-fields 0 condition))
  ([field-config form-fields generation condition]
   (or (nil? condition) ;; short-circuit on no condition
       (> generation 5) ;; break check recursion on too many generations
       (let [check-single-condition? (partial single-condition-true? field-config form-fields (inc generation))]
         (if (sequential? condition)
           ;; for sequential coll one of the conditions has to be met
           (some check-single-condition? condition)
           (check-single-condition? condition))))))

(defn visible?
  "Returns true if the field (last arg) is visible based on the field condition and values of other fields in the form.
   If the field does not have `:condition` key, always returns `true`."
  [field-config {:keys [:form/form-fields]} {:keys [condition]}]
  (condition-true? field-config form-fields condition))

(defn visible-fn [field-config form]
  (fn [form-element]
    (->> (-> form-element second :field)
         (get field-config)
         (visible? field-config form))))

(defn field-value-is-set?
  "Returns true if the given field's EDN encoded value string has a non-empty value"
  [field]
  (let [value (decode-value field)]
    (if (vector? value)
      (every? some? value)
      (-> value is-empty? not))))


(defn get-form-elements [form-schema]
  (tree-seq vector? (partial filter vector?) form-schema))

(defn required? [form-element]
  (-> form-element second :form.field/required (= true)))

(defn get-required-fields [field-type form-schema]
  (->> form-schema
       get-form-elements
       (filter #(-> % first (= field-type)))
       (filter required?)))

(defn required-checkbox-groups-have-value
  "Returns false iff there is one or more checkbox groups for which the next conditions are true:
   - the group is required
   - group has at least one visible checkbox field
   - none of the visible checkbox fields have a value"
  [field-config form]
  (let [form-schema             (get-form-data-schema form)
        form-fields             (:form/form-fields form)
        field-visible?          (visible-fn field-config form)
        get-checkbox-field-name #(and (vector? %)
                                      (-> % first (= :checkbox))
                                      (field-visible? %)
                                      (-> % second :field))]
    (->> form-schema
         (get-required-fields :checkbox-group)
         (every? (fn [[_ _ & children]]
                   (if-let [field-names-for-visible-checkboxes (->> children
                                                                    (map get-checkbox-field-name)
                                                                    (filter keyword?)
                                                                    seq)]
                     (some (fn [field-name]
                             (-> form-fields
                                 (p/find-first #(= field-name (:form.field/id %)))
                                 decode-value
                                 (= true)))
                           field-names-for-visible-checkboxes)
                     true))))))


(defn required-checkboxes-are-checked [field-config form]
  (let [schema (get-form-data-schema form)
        fields (:form/form-fields form)]
    (->> schema
         (get-required-fields :checkbox)
         (filter (visible-fn field-config form))
         (every? (fn [[_ {:keys [field]}]]
                   (-> fields
                       (p/find-first #(= field (:form.field/id %)))
                       decode-value
                       (= true)))))))


(defn required-radio-button-groups-have-value
  "Ensures that mandatory radio button groups have a value that is currently visible"
  [field-config form]
  (let [form-schema                    (get-form-data-schema form)
        form-fields                    (:form/form-fields form)
        field-visible?                 (visible-fn field-config form)
        ;; retrieves the visible radio buttons that are currently allowed as values of the field
        get-radio-button-field-value   #(when (and (vector? %)
                                                   (contains? #{:radio-button :radio-button-custom} (-> % first))
                                                   (field-visible? %))
                                          (-> % second :value))
        ;; retrieves the visible and required radio buttons groups
        visible-required-button-groups (->> form-schema
                                            get-form-elements
                                            (filter required?)
                                            (filter field-visible?)
                                            (filter (comp #{:radio-button-group :radio-button-group-custom} first)))]
    (->> visible-required-button-groups
         (every? (fn [[_ props & children]]
                   (if-let [field-values-for-visible-radio-buttons (->> children
                                                                        (keep get-radio-button-field-value)
                                                                        seq)]
                     (->> field-values-for-visible-radio-buttons
                          (some (fn [val]
                                  (-> form-fields
                                      (p/find-first #(= (:field props) (:form.field/id %)))
                                      decode-value
                                      (= val)))))
                     true))))))


(defn field-is-signature? [field]
  ;; signature is always seq
  (and (-> field :form.field/kind (= :form.field.kind/seq))
       (-> field field-attrs-from-schema :signature-type)))


(defn signature-is-inactive? [signature]
  (boolean
    (when signature
      (du/find-descendant #(-> % :form.field/name (= :signature-inactive))
                          signature))))


(defn field-has-active-signature? [field]
  (when (field-is-signature? field)
    (->> field
         :entity/_parent
         (not-every? signature-is-inactive?))))

(defmulti value-set? (fn [field _] (:form.field/kind field)))

(defmethod value-set? :default [field config]
  (let [is-empty? (if (:rich-text? config)
                    rich-text/empty-html-rich-text?
                    is-empty?)]
    (->> field decode-value is-empty? not)))

(defmethod value-set? :form.field.kind/seq [field _]
  (if (field-is-signature? field)
    (field-has-active-signature? field)
    (->> field
         :entity/_parent
         (some (eu/is-type-fn :entity.type/form-field)))))

(defmethod value-set? :form.field.kind/file-seq [field _]
  (->> field
       :entity/_parent
       (some (eu/is-type-fn :entity.type/form-field))))

(defmethod value-set? :form.field.kind/entity-ref [field _]
  (boolean (:form.field/entity-ref field)))

(defn form-required-fields-set?
  "Check if all visible and required form fields in this form have a value set.

  Note: This optimizes the check by using :form/form-fields and not get-form-fields-raw, which means that in rare
  cases a field that exist in the phase but is not in this form's :form/form-fields would not be found.
  In that case the end-user can resolve this by editing the fields in question, which would cause them to be
  added to :form/form-field."
  ([form] (form-required-fields-set? form #{}))
  ([form field-keys-to-exclude]
   (let [fields                      (->> form
                                          :form/form-fields
                                          (remove #(-> % :form.field/id field-keys-to-exclude)))
         existing-field-ids          (->> fields
                                          (map :form.field/id)
                                          set)
         field-config                (->> form
                                          get-form-data-schema
                                          form-schema/get-fields-from-schema)
         get-field-config            #(->> % :form.field/id (get field-config))
         fields-to-check-from-config (->> field-config
                                          (p/filter-vals :form.field/required)
                                          (p/remove-keys field-keys-to-exclude)
                                          (p/remove-keys existing-field-ids))
         visible-and-required?       (fn [config]
                                       (and (:form.field/required config)
                                            (visible? field-config form config)))]
     ;; Every visible and required field must not be blank
     ;; and every required checkbox group must have at least one checkbox checked
     ;; and some visible radio button must be checked if the button group is required
     (and (->> fields
               (filter #(->> % get-field-config visible-and-required?))
               (every? #(value-set? % (get-field-config %))))
          ;; If a field is a required field in form schema it must exists
          (->> fields-to-check-from-config
               (p/filter-vals visible-and-required?)
               empty?)
          (required-checkboxes-are-checked field-config form)
          (required-checkbox-groups-have-value field-config form)
          (required-radio-button-groups-have-value field-config form)))))


(defn form-ai-updated-at
  "Returns when AI last updated the form, if ever."
  [form]
  (or (:form/ai-updated-at form)
      #?(:clj
         ;; Compatibility fallback for existing unconfirmed NoVa-filled forms. Remove this history lookup
         ;; once those forms can no longer exist; new AI updates persist :form/ai-updated-at directly.
         (some-> (trail/db-last-change-of-command
                   (d/entity-db form)
                   (:db/id form)
                   :app.domain.workspace.form-ai/fill-form-fields)
                 :db/txInstant)
         :cljs nil)))


(defn form-confirmed?
  "True when the form's latest AI update has a valid confirmation.
   Manual edits do not invalidate confirmation; a later AI update does."
  [form]
  (when-let [confirmed-at (:form/confirmed-at form)]
    (if-let [ai-updated-at (form-ai-updated-at form)]
      (>= (inst-ms confirmed-at) (inst-ms ai-updated-at))
      true)))


(defn- submission-awaiting-sign-off?
  [form]
  (= :state.application/submission-awaiting-sign-off
     (some-> form :entity/parent :domain/state)))


(defn- form-writable-by-user?
  [form user]
  (let [granted-permissions  (set (permission-resolver/permissions form user))
        required-permissions (set (:entity/required-write-permissions form))]
    (and (contains? granted-permissions :permission.phase/write)
         (every? granted-permissions required-permissions))))


(defn form-confirmation-satisfied?
  "True when this form does not need confirmation from the given user, or has a valid confirmation.

   A form is exempt from confirmation when any of:
   - It is a sign-off form (has its own gating mechanism).
   - The user does not have write permission on the form (authority-only forms).
   - No form fields have been updated by AI.
   - In submission-awaiting-sign-off, the form has been confirmed before.

   For forms that do require confirmation, it is satisfied when :form/confirmed-at
   is present and >= the latest AI update timestamp."
  [form user]
  (let [sign-off?     (-> form :form/schema :form.schema/type (= :form.schema.type/sign-off))
        writable?     (form-writable-by-user? form user)
        ai-updated-at (form-ai-updated-at form)
        confirmed-at  (:form/confirmed-at form)]
    (boolean
      (or sign-off?
          (not writable?)
          (and (submission-awaiting-sign-off? form)
               confirmed-at)
          (nil? ai-updated-at)
          (when confirmed-at
            (>= (inst-ms confirmed-at) (inst-ms ai-updated-at)))))))


(def is-form? (partial eu/is-type? :entity.type/form))


(defn find-elements-in-schema [[query-tag query-opts :as query] [tag opts & children :as element]]
  (apply concat (when (and (or (nil? query-tag) (= query-tag tag))
                           (or (nil? query-opts) (set/subset? (set query-opts) (set opts))))
                  [element])
         (->> children
              (filter vector?)
              (map (partial find-elements-in-schema query)))))


(defn find-elements
  "Find elements in form by matching tag name and attributes, e.g. [:section {:attr 1}]
   Use nil as a wildcard, e.g. [nil {:attr 1}]"
  [form query]
  (find-elements-in-schema query (get-form-data-schema form)))

(defn field-guide-id
  "A stable id for a field, usable as a `data-guide-id`/`data-guide` value for NoVa's
   guide-highlight mechanism. Shared between `app.front.form.render` and
   `app.domain.workspace.form-ai` so the convention can't drift between the two."
  [field-key]
  (str "form-field-" (name field-key)))

(defn fields-to-map
  "Given form field entities, return a map from field name to value"
  [fields]
  (->> fields
       (map (juxt :form.field/name
                  (comp #?(:clj edn/decode :cljs identity)
                        :form.field/value)))
       (into {})))

(defn determine-signed-by
  "Return user-id who last signed a form field"
  [form-field]
  (let [last-signature (->> form-field
                            :entity/_parent
                            (map :entity/_parent)
                            (map fields-to-map)
                            (sort-by :ts)
                            last)]
    (when-not (:signature-inactive last-signature)
      (:user-id last-signature))))


(defn forms-inline? [forms]
  (and (-> forms count (= 1))
       (->> forms
            (mapcat :form/form-fields)
            count
            (> 3))))


(defn field-kind-is-seq? [field]
  (->> field
       :form.field/kind
       (contains? {:form.field.kind/seq :form.field.kind/file-seq})))


(defn file-input-has-file? [field]
  (->> field
       :entity/_parent
       (filter (eu/is-type-fn :entity.type/form-field))
       (some #(-> %
                  :form.field/entity-ref
                  :entity/type
                  (= :entity.type/file)))))

(defn get-fields-recursively [field]
  (concat [field]
          (mapcat get-fields-recursively (:form.field/_parent field))))

(defn get-form-name-from-id [id]
  (-> id
      keyword
      name
      keyword))

(def form-schema-types
  [:form.schema.type/role-specific
   :form.schema.type/sign-off
   :form.schema.type/general
   :form.schema.type/pre-consultation
   :form.schema.type/execution
   :form.schema.type/periodic
   :form.schema.type/work-specific])

(defn instantiate-for-each-group-config?
  "True, if we should instantiate a separate form for each group config referencing this schema"
  [{schema-type :form.schema/type}]
  (contains? #{:form.schema.type/work-specific} schema-type))

(defn find-form-field-by-id [{:form/keys [form-fields]} field-id]
  (p/find-first form-fields #(-> % :form.field/id (= field-id))))

(defn first-parent-field-with-config
  "Returns the field itself if the field exists in the config-map"
  [config-map {:keys [:entity/type :form.field/id] :as field}]
  (when (= type :entity.type/form-field)
    (if (and id (get config-map id))
      field
      (first-parent-field-with-config config-map (:entity/parent field)))))

;;
;; Helper functions that are used when reading data from form fields
;;

(def forms (child/child-entities-of-type-fn :entity.type/form))

(def ^{:arglists '([phase-entity])} form-field-entities
  (child/child-entities-of-type-fn :entity.type/form-field))

(defn active-form-field-entities
  "This can be used to get the form field entities under a phase that are currently considered active.
   This should be 1:1 faster alternative to get-visible-form-fields because the active/inactive state is calculated
   at modification time."
  [phase-entity]
  (->> (form-field-entities phase-entity)
       (remove :form.field/inactive)))

(defn- field-attrs+field
  "Checks if some field-config-map contains the field id,
  and if the form field is visible either always or based on :condition, returns a tuple of
  [schema-field-attributes field-entity]"
  [field-config-map form-fields {:form.field/keys [id kind parent] :as field}]
  (if (and (= kind :form.field.kind/seq)
           (some-> parent :form.field/kind (= :form.field.kind/seq))
           (field-attrs+field field-config-map form-fields parent))
    ;; This is a table row which has no representation in the schema, so it cannot be found from field-config-map
    [{} field]
    (let [field-attributes (get field-config-map id)
          field-visible?   (->> (:condition field-attributes)
                                (condition-true? field-config-map form-fields))]
      (when (and field-attributes field-visible?)
        [field-attributes field]))))

(defn get-form-fields-raw
  "Returns form fields that are referenced in the schema of the form and in `:form/form-fields` entity attr
   or children of the phase the form is in.
   NOTE! this fn also returns those fields that might be hidden on the actual form"
  [form]
  (let [field-ids-in-schema (form->field-ids-from-schema form)
        fields-from-form    (->> form
                                 :form/form-fields
                                 (filter (comp field-ids-in-schema :form.field/id))
                                 (mapcat get-fields-recursively))
        not-found-field-ids (->> fields-from-form
                                 (keep :form.field/id)
                                 set
                                 (set/difference field-ids-in-schema))]
    (cond-> fields-from-form
      ;; this should be quite rare, but the form schema can reference fields that exist under the phase but have not
      ;; been added to :form/form-fields, if the schema has changed after the form was created
      (seq not-found-field-ids)
      (concat (->> form
                   :entity/parent
                   :form.field/_parent
                   (filter (comp not-found-field-ids :form.field/id))
                   (mapcat get-fields-recursively))))))

;;
;; Functions for reading data from (visible) form fields.
;;
;; Q: What does it mean that a form field is visible?
;; A: It depends on the context:
;;    - A form field is visible on a form if the field would be visible when rendering the form
;;    - A form field is visible on an application if there exists a form where the field is visible

(defn get-field-attrs+field-tuples
  "- Input entity can be a phase entity or a form
   - Returns a seq of tuples: [[field-attrs-from-schema field-entity]...]
   - Only visible fields are included in the result
   - NOTE: If `entity` is a phase and there are two conflicting schema definitions for the same field,
           all definitions (duplicate entities) will be returned."
  ([entity]
   (get-field-attrs+field-tuples entity nil))
  ([entity field-id-or-ids]
   (if (workspace-model/is-phase-entity? entity)
     (->> (forms entity)
          (mapcat #(get-field-attrs+field-tuples % field-id-or-ids))
          distinct)
     (let [field-ids-to-keep (when field-id-or-ids
                               (p/wrap-into #{} field-id-or-ids))
           field-config-map  (form->field-config-map entity)
           form-fields       (get-form-fields-raw entity)]
       (->> form-fields
            (keep (fn [{:form.field/keys [id] :as field}]
                    (when (or (nil? field-ids-to-keep)
                              (contains? field-ids-to-keep id))
                      (field-attrs+field field-config-map form-fields field)))))))))


(defn get-visible-form-fields
  "Input entity can be a phase entity or a form"
  [entity]
  (->> (get-field-attrs+field-tuples entity)
       (map second)
       distinct))

(defn visible-table-column-keys
  "Expected cells in each visible table, including columns whose field entity has not been created yet."
  [form]
  (let [configs (form->field-config-map form)]
    (->> (get-field-attrs+field-tuples form)
         (keep (fn [[{:keys [tag columns]} field]]
                 (when (= :table tag)
                   [(:db/id field)
                    (->> columns
                         (filter #(visible? configs form (get configs (:field %))))
                         (map #(hash-map :form.field/id (:field %)
                                         :form.field/kind (case (:tag %)
                                                            :file-input :form.field.kind/file-seq
                                                            :personal-id :form.field.kind/encrypted-personal-id
                                                            (:table :signature) :form.field.kind/seq
                                                            :form.field.kind/val)))
                         set)])))
         (into {}))))


(defn get-field-attrs+field-val
  "- Input entity can be a phase entity or a form
   - Tuple [field-attrs-from-schema field] is returned if the field is visible; otherwise, returns nil"
  [entity field-id]
  (some-> (get-field-attrs+field-tuples entity field-id)
          first
          (update 1 (comp str-util/blank->nil decode-value))))

(defn get-form-field
  "- Input entity can be a phase entity or a form
   - Returns the form field if the field is visible; otherwise, returns nil"
  [entity field-id]
  (-> (get-field-attrs+field-tuples entity field-id) first second))

(defn get-val-of-form-field
  "- Input entity can be a phase entity or a form
   - Returns the encoded value of the form field if the field is visible; otherwise, returns nil"
  [entity field-id]
  (-> (get-field-attrs+field-val entity field-id) second))

(defn form-field-for-ws
  "Try to find the field under any workspace phase, from latest to first."
  [workspace field]
  (or (some-> workspace workspace-model/workspace-execution (get-field-attrs+field-val field))
      (some-> workspace workspace-model/workspace-application (get-field-attrs+field-val field))
      (some-> workspace workspace-model/own-or-prior-pre-consultation (get-field-attrs+field-val field))))


(defn form-field-attrs+entity-for-ws
  "Try to find the field entity under any workspace phase, from latest to first."
  [workspace field]
  (or (some-> workspace workspace-model/workspace-execution (get-field-attrs+field-tuples field) first)
      (some-> workspace workspace-model/workspace-application (get-field-attrs+field-tuples field) first)
      (some-> workspace workspace-model/own-or-prior-pre-consultation (get-field-attrs+field-tuples field) first)))


(defn- form-field-child [parent field-id]
  (p/find-first (:form.field/_parent parent) {:form.field/id field-id}))


(defn- table-row? [field]
  (-> field :form.field/kind (= :form.field.kind/seq)))


(defn table-field-data-for-ws
  "Return renderable table data for a table `field` id under `workspace`, or nil if not found.

  Returns `{:headers [..strings], :columns [..column-configs], :rows [[cell ..] ..]}`.
  `:columns` are the table's render-shape column configs (see
  `app.domain.form.schema/table-columns`); `:headers` are the table's column header strings.
  Each row is a vector of cells aligned positionally to `:columns`; a cell is its column config
  plus `:form-field` (the stored cell entity) and `:value` (its decoded value). Rows are ordered
  by `:form.field/index`.

  Cells are resolved STRUCTURALLY: values are read from the actual cell entities under each row
  (`form-field-child` scoped to the row), not by bare field id. So a column id that also exists
  elsewhere in the schema (top level, or another table) does not confuse resolution — table cell
  ids are only meaningful within their table, which is why they are intentionally not offered as
  standalone selectable/searchable fields."
  [workspace field]
  (when-let [[{:keys [headers columns]} table-field] (form-field-attrs+entity-for-ws workspace field)]
    {:headers headers
     :columns columns
     :rows    (->> table-field
                   :form.field/_parent
                   (filter table-row?)
                   (sort-by :form.field/index)
                   (map (fn [row]
                          (mapv (fn [{:keys [field] :as column}]
                                  (let [cell (form-field-child row field)]
                                    (assoc column
                                           :form-field cell
                                           :value (decode-value cell))))
                                columns))))}))

(defn form-field-value-for-ws
  "Try to find a value for the field under any workspace phase."
  [workspace field]
  (-> (form-field-for-ws workspace field) second))

(defn form-field->value [form-field]
  (some-> form-field
          decode-value
          str-util/blank->nil
          rich-text/html-to-text-if-string))

(defn find-field-value
  "Only should be used after hidden fields are removed, i.e.
  DO NOT CALL THIS FUNCTION LIKE THIS (-> form :form/form-fields (find-field-value :field-id-1))"
  [visible-form-fields field-id]
  (some-> visible-form-fields
          (p/find-first {:form.field/id field-id})
          form-field->value))

(defn- form-has-active-signature-internal? [form]
  (some field-has-active-signature? (get-visible-form-fields form)))

(def ^{:arglists '([form])} form-has-active-signature?
  #?(:clj  form-has-active-signature-internal?
     :cljs (futil/single-memoize form-has-active-signature-internal?)))

(defmethod du/is-active? :entity.type/form-schema
  [{state        :domain/state
    feature-flag :form.schema/feature-flag
    deprecated?  :entity/deprecated?
    :as          #?(:clj form-schema :cljs _)}]
  (and (not= state :form-schema.state/removed)
       (or (nil? feature-flag)
           #?(:clj  (feature-flag/flag-enabled-or-removed? (d/entity-db form-schema) feature-flag)
              :cljs (ff/enabled-or-removed? feature-flag)))
       ;; TODO we don't seem to set deprecated anywhere
       (not deprecated?)))

(defmethod group-model/get-authority-group :entity.type/form
  [form]
  (-> form :entity/parent group-model/get-authority-group))

(def ^{:arglists '([authority-group])} group-form-schemas
  (group-model/entities-of-group-fn :entity.type/form-schema))

(defn label-text [label lang]
  (cond
    (string? label) label
    (map? label) (i18n-model/get-text-from-entities-or-map label lang)))

(defn file-input-latest-file [field]
  (->> field
       form-field-entities
       (sort-by :form.field/index)
       last
       :form.field/entity-ref))

(defn is-type? [expected-type schema]
  (-> schema :form.schema/type (= expected-type)))

(defn form-label
  "Get form name with party- or group-config name appended as needed"
  ([{:form/keys [schema] :as form} lang]
   (form-label form lang (:form.schema/name schema)))
  ([{:form/keys [party group-config schema]} lang form-name]
   (let [party-name (when (is-type? :form.schema.type/role-specific schema)
                      (some-> party (party-model/simple-name lang)))
         trade-name (when (is-type? :form.schema.type/work-specific schema)
                      (some-> group-config (config-model/localized-name lang)))]
     (str form-name
          (when party-name
            (str ": " party-name))
          (when trade-name
            (str ": " trade-name))))))

(defn find-existing-field
  "Find existing field based on the compound id.
   `index` and `name` parts of the compound id are not currently supported, and must be `nil`
   Compound id vector is expected to be in the same order as :form.field/id+index+name+parent+party+group-config"
  [existing-fields [id index name parent-id party-id group-config-id]]
  {:pre [(nil? index) (nil? name)]}
  ;; name is not checked because due to an old bug hundreds of thousands of imported form fields in production contain
  ;; the name attribute even when they should not
  (let [path+vs [[[:form.field/id] id]
                 [[:form.field/parent :db/id] parent-id]
                 [[:form.field/party :db/id] party-id]
                 [[:form.field/group-config :db/id] group-config-id]]]
    (p/find-first existing-fields
                  #(->> path+vs
                        (every? (fn [[path v]]
                                  (= (get-in % path) v)))))))
